/**
 * InboxDemo - Demo component showing the inbox UI layout and components
 * This is for the Design system page to preview inbox components
 */

import { useState } from 'react'
import { SidebarProvider, SidebarInset } from '@/components/ui/shadcn/sidebar'
import { AppSidebar } from '@/components/Layout/AppSidebar'
import { ConversationListItem } from './ConversationListItem'
import { MessageBubble } from './MessageBubble'
import { Avatar } from '@/components/ui/Avatar'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import {
  Send,
  Paperclip,
  MoreHorizontal,
  Check,
  Globe,
  MapPin,
  Monitor,
  Clock,
} from 'lucide-react'
import { getBrowserIcon } from './drawer/BrowserIcon'


// Old format for ConversationListItem component
const mockConversations = [
  {
    id: 'conv_1',
    visitorName: 'Isaac Wendt',
    visitorEmail: 'isaac@example.com',
    lastMessage: 'Thanks!',
    lastMessageAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    unreadCount: 0,
    isResolved: true,
  },
  {
    id: 'conv_2',
    visitorName: 'Cleary Mooney',
    visitorEmail: 'cleary@example.com',
    lastMessage: 'Hey, noticed an online singles event...',
    lastMessageAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    unreadCount: 2,
    isResolved: false,
  },
  {
    id: 'conv_3',
    visitorName: 'Benjamin LEGENTIL',
    visitorEmail: 'ben@example.com',
    lastMessage: "Ah ok I'll check. Might be how B...",
    lastMessageAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    unreadCount: 0,
    isResolved: false,
  },
  {
    id: 'conv_4',
    visitorName: 'Richard',
    visitorEmail: 'richard@example.com',
    lastMessage: "Thanks. I'll have to look into it...",
    lastMessageAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    unreadCount: 0,
    isResolved: false,
  },
]

// Messages in new format for MessageBubble component
const mockMessages = [
  {
    message: {
      id: 'msg_1',
      content: 'Bonjour, j\'ai un problème avec ma commande',
      blurred: false,
      message_type: 'text' as const,
      sender_type: 'Visitor' as const,
      sender_name: 'Isaac Wendt',
      sender_avatar_url: null,
      created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
      source_language: 'fr',
      translation: null,
      metadata: null,
      attachment: null,
    },
    isFromAgent: false,
    translation: {
      translated: 'Hello, I have a problem with my order',
      sourceLanguage: 'fr',
      sourceLanguageName: 'French',
    },
  },
  {
    message: {
      id: 'msg_2',
      content: "I'd be happy to help! What's the order number?",
      blurred: false,
      message_type: 'text' as const,
      sender_type: 'User' as const,
      sender_name: 'Colin Goudie',
      sender_avatar_url: null,
      created_at: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
      source_language: null,
      translation: null,
      metadata: null,
      attachment: null,
    },
    isFromAgent: true,
  },
  {
    message: {
      id: 'msg_3',
      content: "C'est la commande #12345",
      blurred: false,
      message_type: 'text' as const,
      sender_type: 'Visitor' as const,
      sender_name: 'Isaac Wendt',
      sender_avatar_url: null,
      created_at: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
      source_language: 'fr',
      translation: null,
      metadata: null,
      attachment: null,
    },
    isFromAgent: false,
    translation: {
      translated: "It's order #12345",
      sourceLanguage: 'fr',
      sourceLanguageName: 'French',
    },
  },
  {
    message: {
      id: 'msg_5',
      content: 'Merci beaucoup!',
      blurred: false,
      message_type: 'text' as const,
      sender_type: 'Visitor' as const,
      sender_name: 'Isaac Wendt',
      sender_avatar_url: null,
      created_at: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
      source_language: 'fr',
      translation: null,
      metadata: null,
      attachment: null,
    },
    isFromAgent: false,
    translation: {
      translated: 'Thank you very much!',
      sourceLanguage: 'fr',
      sourceLanguageName: 'French',
    },
  },
]

const mockVisitor = {
  name: 'Isaac Wendt',
  email: 'isaac@example.com',
  location: 'Sydney, NSW, Australia',
  timezone: 'UTC+10',
  device: 'Windows',
  browser: 'Chrome 143',
  locale: 'fr-FR',
  firstSeen: '10 December',
  lastActive: '5 minutes ago',
}

export function InboxDemo() {
  const [activeConversation, setActiveConversation] = useState('conv_1')
  const [messageInput, setMessageInput] = useState('')
  const MockBrowserIcon = getBrowserIcon(mockVisitor.browser)

  return (
    <div className="h-[600px] border rounded-lg overflow-hidden bg-background">
      <SidebarProvider defaultOpen={true}>
        <AppSidebar />
        <SidebarInset className="flex">
          {/* Conversation List */}
          <div className="w-80 border-r flex flex-col">
            {/* List Header */}
            <div className="p-3 border-b">
              <div className="flex items-center justify-between">
                <button className="flex items-center gap-2 text-sm font-medium hover:bg-muted px-2 py-1 rounded cursor-pointer">
                  Unresolved
                  <span className="text-xs text-muted-foreground bg-muted px-1.5 rounded">
                    {mockConversations.filter((c) => !c.isResolved).length}
                  </span>
                </button>
              </div>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {mockConversations.map((conv) => (
                <ConversationListItem
                  key={conv.id}
                  {...conv}
                  isActive={activeConversation === conv.id}
                  onClick={() => {
                    setActiveConversation(conv.id)
                    setMessageInput('')
                  }}
                />
              ))}
            </div>
          </div>

          {/* Conversation View */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Conversation Header */}
            <div className="p-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar name={mockVisitor.name} size="sm" />
                <div>
                  <h3 className="font-medium">{mockVisitor.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Globe className="size-3" />
                    <span>French (FR)</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">
                  <Check className="size-4 mr-1" />
                  Resolved
                </Button>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="size-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
              {mockMessages.map((msg) => (
                <MessageBubble
                  key={msg.message.id}
                  message={msg.message}
                  isFromAgent={msg.isFromAgent}
                  translation={msg.translation}
                />
              ))}
            </div>

            {/* Message Input */}
            <div className="p-3 border-t">
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <Input
                    placeholder={`Send your message to ${mockVisitor.name}...`}
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    className="pr-10"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 size-8"
                  >
                    <Paperclip className="size-4" />
                  </Button>
                </div>
                <Button size="icon">
                  <Send className="size-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Visitor Panel */}
          <div className="w-72 border-l p-4 overflow-y-auto">
            {/* Visitor Header */}
            <div className="text-center mb-6">
              <Avatar
                name={mockVisitor.name}
                email={mockVisitor.email}
                size="xl"
                className="mx-auto mb-3"
              />
              <h3 className="font-semibold text-lg">{mockVisitor.name}</h3>
              <p className="text-sm text-muted-foreground">{mockVisitor.email}</p>
            </div>

            {/* Visitor Info */}
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Main Information
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="size-4 text-muted-foreground" />
                    <span>{mockVisitor.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="size-4 text-muted-foreground" />
                    <span>{mockVisitor.timezone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="size-4 text-muted-foreground" />
                    <span>{mockVisitor.locale}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Monitor className="size-4 text-muted-foreground" />
                    <span>{mockVisitor.device}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MockBrowserIcon className="size-4 text-muted-foreground" />
                    <span>{mockVisitor.browser}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Activity
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">First seen</span>
                    <span>{mockVisitor.firstSeen}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last active</span>
                    <span>{mockVisitor.lastActive}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  )
}

export default InboxDemo
